import { z } from 'zod';

/*
 * The text of a publication by blocks, as the brain writes it and the engine
 * reads it (order.json blocks, spec 5.2). Limits are never constants here: they
 * come from the copy of the engine passport (decision 25).
 */

export const CONTENT_FORMATS = ['reel', 'carousel'] as const;
export type ContentFormat = (typeof CONTENT_FORMATS)[number];

export const BLOCK_ROLES = ['hook', 'body', 'cta'] as const;
export type BlockRole = (typeof BLOCK_ROLES)[number];

/** The voice manner the brain suggests for a reel (spec 8.4). */
export const MANNERS = ['calm', 'energetic', 'confiding'] as const;
export type Manner = (typeof MANNERS)[number];

export const BLOCK_ID_RE = /^[A-Za-z0-9_-]{1,32}$/;

/** A block of order.json without `spoken` (stage 4 adds it). */
export const contentBlockSchema = z
  .object({
    id: z.string().regex(BLOCK_ID_RE),
    role: z.enum(BLOCK_ROLES),
    text: z.string().min(1),
    title: z.string().min(1).optional(),
  })
  .strict();
export type ContentBlock = z.infer<typeof contentBlockSchema>;

/** Ids b1…bN, given by us on every new version. */
export function assignBlockIds(blocks: Omit<ContentBlock, 'id'>[]): ContentBlock[] {
  return blocks.map((b, i) => ({ id: `b${i + 1}`, ...b }));
}

/** A statement of the text the author did not make. */
export const draftFlagSchema = z.object({
  id: z.string(),
  blockId: z.string(),
  quote: z.string(),
  reason: z.string(),
  state: z.enum(['open', 'kept']),
});
export type DraftFlag = z.infer<typeof draftFlagSchema>;

/** What the model would add if the author confirms. */
export const draftSuggestionSchema = z.object({
  id: z.string(),
  text: z.string(),
  state: z.enum(['open', 'added', 'dismissed']),
});
export type DraftSuggestion = z.infer<typeof draftSuggestionSchema>;

// ---- passport limits ---------------------------------------------------------

const count = z.number().int().nonnegative();
const range = z.tuple([count, count]);
const roleLimit = z.object({ max_chars: count });
const slideLimit = z.object({ title_max_chars: count, text_max_chars: count });

export const reelFormatSchema = z.object({
  max_duration_s: z.number().positive(),
  blocks: range,
  words_per_second: z.number().positive(),
  roles: z.object({ hook: roleLimit, body: roleLimit, cta: roleLimit }),
});

export const carouselFormatSchema = z.object({
  slides: range,
  title_max_chars: count,
  text_max_chars: count,
  /** Engine 0.3.0+: the last slide is shorter; older passports have one limit for all. */
  roles: z.object({ hook: slideLimit, body: slideLimit, cta: slideLimit }).optional(),
});

/** The top `formats` of passport.json (engine ≤ 0.4.0; the same as «Сцены» later). */
export const passportFormatsSchema = z.object({ reel: reelFormatSchema, carousel: carouselFormatSchema });

/** `types[].formats` of passport.json (engine 0.5.0+): a type may have no carousel. */
export const typeFormatsSchema = z.object({ reel: reelFormatSchema, carousel: carouselFormatSchema.optional() });
export type TypeFormats = z.infer<typeof typeFormatsSchema>;

const slideLimits = z.object({ titleMaxChars: z.number(), textMaxChars: z.number() });

export const textLimitsSchema = z.object({
  reel: z.object({
    maxDurationS: z.number(),
    minBlocks: z.number(),
    maxBlocks: z.number(),
    wordsPerSecond: z.number(),
    maxChars: z.object({ hook: z.number(), body: z.number(), cta: z.number() }),
  }),
  /** null: this reel type has no carousel («Схема»). */
  carousel: z
    .object({
      minSlides: z.number(),
      maxSlides: z.number(),
      titleMaxChars: z.number(),
      textMaxChars: z.number(),
      roles: z.object({ hook: slideLimits, body: slideLimits, cta: slideLimits }),
    })
    .nullable(),
});
export type TextLimits = z.infer<typeof textLimitsSchema>;

/** The text limits of one type's formats (the passport is read in engine/passport.ts). */
export function limitsFromFormats(formats: TypeFormats): TextLimits {
  const { reel, carousel } = formats;
  const slide = (c: NonNullable<typeof carousel>, role: BlockRole) => {
    const own = c.roles?.[role];
    return {
      titleMaxChars: own?.title_max_chars ?? c.title_max_chars,
      textMaxChars: own?.text_max_chars ?? c.text_max_chars,
    };
  };
  return {
    reel: {
      maxDurationS: reel.max_duration_s,
      minBlocks: reel.blocks[0],
      maxBlocks: reel.blocks[1],
      wordsPerSecond: reel.words_per_second,
      maxChars: {
        hook: reel.roles.hook.max_chars,
        body: reel.roles.body.max_chars,
        cta: reel.roles.cta.max_chars,
      },
    },
    carousel: carousel
      ? {
          minSlides: carousel.slides[0],
          maxSlides: carousel.slides[1],
          titleMaxChars: carousel.title_max_chars,
          textMaxChars: carousel.text_max_chars,
          roles: { hook: slide(carousel, 'hook'), body: slide(carousel, 'body'), cta: slide(carousel, 'cta') },
        }
      : null,
  };
}

// ---- counting ----------------------------------------------------------------

/** Code points of the trimmed text, as the engine counts them. */
export function countChars(s: string): number {
  return [...s.trim()].length;
}

/** Whitespace-separated tokens with a letter or a digit: a lone dash is not a word. */
export function countWords(s: string): number {
  return s
    .split(/\s+/)
    .filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
}

export function estimateReelSeconds(blocks: ReadonlyArray<{ text: string }>, wordsPerSecond: number): number {
  const words = blocks.reduce((n, b) => n + countWords(b.text), 0);
  return Math.round((words / wordsPerSecond) * 10) / 10;
}

// ---- form check (no AI) ------------------------------------------------------

export const formIssueSchema = z.discriminatedUnion('code', [
  z.object({ code: z.literal('empty_block'), blockId: z.string() }),
  z.object({ code: z.literal('block_too_long'), blockId: z.string(), max: z.number(), actual: z.number() }),
  z.object({ code: z.literal('title_too_long'), blockId: z.string(), max: z.number(), actual: z.number() }),
  z.object({ code: z.literal('title_not_allowed'), blockId: z.string() }),
  z.object({ code: z.literal('no_letters'), blockId: z.string() }),
  z.object({ code: z.literal('too_few_blocks'), min: z.number(), actual: z.number() }),
  z.object({ code: z.literal('too_many_blocks'), max: z.number(), actual: z.number() }),
  z.object({ code: z.literal('bad_roles') }),
  z.object({ code: z.literal('reel_too_long'), maxS: z.number(), estimatedS: z.number() }),
]);
export type FormIssue = z.infer<typeof formIssueSchema>;

const expectedRole = (i: number, n: number): BlockRole => (i === 0 ? 'hook' : i === n - 1 ? 'cta' : 'body');

/**
 * What the engine would refuse in this text, by the passport limits: per block
 * in block order, then the count, the roles and (reel) the duration. A carousel
 * of a type without one throws: a bot bug, never the person's text.
 */
export function checkDraftForm(
  format: ContentFormat,
  blocks: ReadonlyArray<ContentBlock>,
  limits: TextLimits,
): FormIssue[] {
  const carousel = limits.carousel;
  if (format === 'carousel' && carousel === null) throw new Error('this type has no carousel');
  const issues: FormIssue[] = [];
  for (const b of blocks) {
    const chars = countChars(b.text);
    if (chars === 0) {
      issues.push({ code: 'empty_block', blockId: b.id });
      continue;
    }
    if (format === 'reel') {
      const max = limits.reel.maxChars[b.role];
      if (chars > max) issues.push({ code: 'block_too_long', blockId: b.id, max, actual: chars });
      if (!/\p{L}/u.test(b.text)) issues.push({ code: 'no_letters', blockId: b.id });
      if (b.title !== undefined) issues.push({ code: 'title_not_allowed', blockId: b.id });
    } else {
      const slide = carousel!.roles[b.role];
      const max = slide.textMaxChars;
      if (chars > max) issues.push({ code: 'block_too_long', blockId: b.id, max, actual: chars });
      if (b.title !== undefined) {
        const titleMax = slide.titleMaxChars;
        const titleChars = countChars(b.title);
        if (titleChars > titleMax) {
          issues.push({ code: 'title_too_long', blockId: b.id, max: titleMax, actual: titleChars });
        }
      }
    }
  }

  const [min, max] =
    format === 'reel'
      ? [limits.reel.minBlocks, limits.reel.maxBlocks]
      : [carousel!.minSlides, carousel!.maxSlides];
  if (blocks.length < min) issues.push({ code: 'too_few_blocks', min, actual: blocks.length });
  if (blocks.length > max) issues.push({ code: 'too_many_blocks', max, actual: blocks.length });

  if (blocks.some((b, i) => b.role !== expectedRole(i, blocks.length))) issues.push({ code: 'bad_roles' });

  if (format === 'reel') {
    const estimatedS = estimateReelSeconds(blocks, limits.reel.wordsPerSecond);
    if (estimatedS > limits.reel.maxDurationS) {
      issues.push({ code: 'reel_too_long', maxS: limits.reel.maxDurationS, estimatedS });
    }
  }
  return issues;
}

// ---- thoughts ----------------------------------------------------------------

/** A thought is at most 5 minutes of audio (spec 6.4). */
export const MAX_THOUGHT_SECONDS = 300;
export const MIN_TEXT_THOUGHT_CHARS = 40;
export const MAX_TEXT_THOUGHT_CHARS = 10_000;
